import React, { useState, ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  AlertIcon,
  AlertDescription,
  CloseButton,
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  Button,
  Select,
  Box,
  Textarea,
  Text,
  Flex,
  Container,
  Center,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import Footer from "./Footer/Footer";

const ENDPOINT = process.env.REACT_APP_ENDPOINT || "";

const Form = () => {
  // State
  const [roleType, setRoleType] = useState("");
  const [remote, setRemote] = useState("");
  const [description, setDescription] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // React router navigation
  let navigate = useNavigate();

  // React hook form
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm();

  /**
   * Update state for description text area
   * @param e event
   */
  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    let inputValue = e.target.value;
    setDescription(inputValue);
  };

  /**
   * Take the form values and submit them to gateway
   * @param object form values
   */
  const onSubmit = async ({
    name,
    email,
    findMe,
    test,
    company,
    title,
    remote,
    type,
    salaryMin,
    salaryMax,
    jobDescription,
  }: any) => {
    // We use JSON.stringify here so the data can be sent as a string via HTTP
    const body = JSON.stringify({
      name,
      email,
      findMe,
      test,
      company,
      title,
      remote,
      type,
      salaryMin,
      salaryMax,
      jobDescription,
    });

    const requestOptions = {
      method: "POST",
      body,
    };

    const axiosConfig = {
      headers: {
        method: "POST",
      },
    };

    // const data = {
    //   body: body,
    // };

    // const headers = {
    //   "Content-Type": "text/plain",
    // };

    // try {
    //   let res = await axios.post(endpoint, body, axiosConfig);
    //   console.log(res);
    // } catch (error) {
    //   console.log(error);
    // }

    // const sendFormRequest = async () => {
    //   let response = await fetch(endpoint, requestOptions);

    //   if (!response.ok) {
    //     throw new Error(`Error! Status: ${response.status}`);
    //   }
    //   return response.json();
    //   alert("Form successfully submitted!");
    // };

    // try {
    //   sendFormRequest();
    // } catch (error) {
    //   alert("An error has occurred. Please resubmit the form.");
    // }

    // Another Axios request attempt (not working blocked by CORS)
    // const PROXY_URL = "https://cors-anywhere.herokuapp.com/";

    // axios.post(PROXY_URL + endpoint, { body }).then(
    //   (response) => {
    //     console.log(response);
    //   },
    //   (error) => {
    //     console.log(error);
    //   }
    // );

    setSubmitting(true);

    // Working version will send the request correctly
    fetch(ENDPOINT, requestOptions)
      .then((response) => {
        if (!response.ok) throw new Error("Error in fetch");
        return {
          statusCode: 200,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify("Success"),
        };
      })
      .then((response) => {
        setSubmitting(false);
        navigate("/success");
      })
      .catch((error) => {
        setSubmitError(error);
        setSubmitting(false);
      });
  };

  return (
    <Container maxW="container.xl" centerContent>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex maxW="2xl">
          <Text
            fontSize="xl"
            mt={2}
            textAlign="center"
            color="primary.800"
            opacity="0.6"
          >
            All fields are required. Please include as much information as
            possible when completing the form. If you have multiple roles you
            must fill out the form for each role. All valid submissions will be
            reviewed as soon as possible. Looking forward to hearing about your
            opportunities!
          </Text>
        </Flex>
        <Flex>
          <FormControl m={2} isInvalid={errors.name}>
            <FormLabel htmlFor="name">Your Name</FormLabel>
            <Input
              id="name"
              {...register("name", {
                required: "Please enter your name",
              })}
            />
            <FormErrorMessage>
              {errors.name && errors.name.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl m={2} isInvalid={errors.email}>
            <FormLabel htmlFor="email">Your Email</FormLabel>
            <Input
              id="email"
              {...register("email", {
                required: "Please enter your email",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Please enter your email",
                },
              })}
            />
            <FormErrorMessage>
              {errors.email && errors.email.message}
            </FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex>
          <FormControl m={2} isInvalid={errors.findMe}>
            <FormLabel htmlFor="findMe">How did you find me?</FormLabel>
            <Input
              id="findMe"
              {...register("findMe", {
                required: "Please enter how you found me",
              })}
            />
            <FormErrorMessage>
              {errors.findMe && errors.findMe.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl m={2} isInvalid={errors.test}>
            <FormLabel htmlFor="test">What is my current title?</FormLabel>
            <Input
              id="test"
              {...register("test", {
                required: "Hint: LinkedIn profile intro",
                pattern: {
                  value: /software engineer/i,
                  message: "Hint: LinkedIn profile intro",
                },
              })}
            />
            <FormErrorMessage>
              {errors.test && errors.test.message}
            </FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex>
          <FormControl m={2} isInvalid={errors.company}>
            <FormLabel htmlFor="company">Company Name</FormLabel>
            <Input
              id="company"
              {...register("company", {
                required: "Please enter the company name",
              })}
            />
            <FormErrorMessage>
              {errors.company && errors.company.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl m={2} isInvalid={errors.title}>
            <FormLabel htmlFor="title">Job Title</FormLabel>
            <Input
              id="title"
              {...register("title", {
                required: "Please enter the job title",
              })}
            />
            <FormErrorMessage>
              {errors.title && errors.title.message}
            </FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex>
          <FormControl m={2} isInvalid={errors.remote}>
            <FormLabel htmlFor="remote">Fully Remote?</FormLabel>
            <Select
              placeholder="Select"
              {...register("remote", {
                required: "Please select one",
                pattern: {
                  value: /Yes/,
                  message: "Only open to fully remote positions",
                },
              })}
              onChange={(e) => setRemote(e.target.value)}
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </Select>
            <FormErrorMessage>
              {errors.remote && errors.remote.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl m={2} isInvalid={errors.type}>
            <FormLabel htmlFor="type">Employment Type</FormLabel>
            <Select
              placeholder="Select"
              {...register("type", {
                required: "Please select one",
                pattern: {
                  value: /FullTime/,
                  message: "Only open to full time roles",
                },
              })}
              onChange={(e) => setRoleType(e.target.value)}
            >
              <option value="FullTime">Full time</option>
              <option value="Contract">Contract</option>
            </Select>
            <FormErrorMessage>
              {errors.type && errors.type.message}
            </FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex>
          <FormControl m={2} isInvalid={errors.salaryMin}>
            <FormLabel htmlFor="salaryMin">Minimum Salary</FormLabel>
            <Input
              id="salary-min"
              type="number"
              {...register("salaryMin", {
                required: "Please enter a minimum salary",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Please enter a number",
                },
              })}
            />
            <FormErrorMessage>
              {errors.salaryMin && errors.salaryMin.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl m={2} isInvalid={errors.salaryMax}>
            <FormLabel htmlFor="salaryMax">Maximum Salary</FormLabel>
            <Input
              id="salary-max"
              type="number"
              {...register("salaryMax", {
                required: "Please enter a maximum salary",
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Please enter a number",
                },
              })}
            />
            <FormErrorMessage>
              {errors.salaryMax && errors.salaryMax.message}
            </FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex>
          <FormControl m={2} isInvalid={errors.jobDescription}>
            <FormLabel htmlFor="description">Job Description</FormLabel>
            <Textarea
              id="job-description"
              value={description}
              {...register("jobDescription", {
                required: "Please enter a job description",
              })}
              onChange={handleDescriptionChange}
              size="sm"
            />
            <FormErrorMessage>
              {errors.jobDescription && errors.jobDescription.message}
            </FormErrorMessage>
          </FormControl>
        </Flex>
        <Box mb={10}>
          <Center>
            <Button
              mt={4}
              colorScheme="teal"
              isLoading={isSubmitting}
              type="submit"
            >
              Submit
              {submitting && <Spinner ml={2} speed="0.8s" size="sm" />}
            </Button>
          </Center>
          <Box mt={4}>
            {submitError && (
              <Alert status="error">
                <AlertIcon />
                <AlertDescription mr={5}>
                  Unable to submit please try again...
                </AlertDescription>
                <CloseButton
                  onClick={() => setSubmitError("")}
                  position="absolute"
                  right="8px"
                  top="8px"
                />
              </Alert>
            )}
          </Box>
        </Box>
        <Divider />
        <Footer />
      </form>
    </Container>
  );
};

export default Form;
